/**
 * Server functions behind the coach's Lead magnets page: read, turn on or off,
 * and set the categories of her own Wheel of Life page. The coach comes from
 * the Clerk session only (no GHL `locationId` fallback), and every call
 * requires the `coach_wheel_pages` flag for her user.
 *
 * Aliases: lead magnets server functions, coach wheel settings, activate
 * coach wheel
 */

import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware, type AuthUser } from "~/lib/auth-middleware";
import { CoachWheelLabelsSchema } from "~/lib/shared/coach-wheel";

/**
 * The signed-in coach's id, with the `coach_wheel_pages` flag on for her.
 * Server-only: only handler bodies are compiled out of the client build.
 */
export const requireWheelCoach = createServerOnlyFn(
  async (user: AuthUser): Promise<string> => {
    const { getCoachByClerkUserId } = await import("~/lib/server/coach");
    const { evaluateFeatureFlag } = await import("~/lib/server/feature-flags");
    const { COACH_WHEEL_FLAG } =
      await import("~/lib/server/wheel/coach-assessment");
    const [coach, flag] = await Promise.all([
      getCoachByClerkUserId(user.clerkUserId),
      evaluateFeatureFlag(COACH_WHEEL_FLAG, user.id),
    ]);
    if (!coach) throw new Error("Coach account required");
    if (!flag.enabled) throw new Error("Coach Wheel pages are not available");
    return coach.id;
  },
);

export const getMyCoachWheelFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const coachId = await requireWheelCoach(context.user);
    const { getCoachWheelSettings } =
      await import("~/lib/server/wheel/coach-assessment");
    const settings = await getCoachWheelSettings(coachId);
    if (!settings) throw new Error("Coach not found");
    return settings;
  });

export const setMyCoachWheelActiveFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ active: z.boolean() }))
  .handler(async ({ data, context }) => {
    const coachId = await requireWheelCoach(context.user);
    const { setCoachWheelActive } =
      await import("~/lib/server/wheel/coach-assessment");
    return setCoachWheelActive(coachId, data.active);
  });

/** `labels: null` goes back to the standard eight. */
export const setMyCoachWheelLabelsFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ labels: CoachWheelLabelsSchema.nullable() }))
  .handler(async ({ data, context }) => {
    const coachId = await requireWheelCoach(context.user);
    const { setCoachWheelLabels } =
      await import("~/lib/server/wheel/coach-assessment");
    await setCoachWheelLabels(coachId, data.labels);
  });
